import { useState, useEffect, useCallback, useMemo } from 'react'
import { RefreshCw, X, AlertTriangle, ExternalLink } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import '../SponsorManager.css'

// AdSpeed exposes campaigns as read-only via the public API
// (AS.Campaigns.getList + getCounts only). No detail page, no create/
// edit — admins manage campaigns directly in the AdSpeed dashboard.

function campaignField(c, ...keys) {
  for (const k of keys) {
    if (c?.[k] != null) return c[k]
  }
  return ''
}

const STATUS_TABS = [
  { value: '',         label: 'All' },
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'archived', label: 'Archived' },
]

export default function CampaignsList() {
  const { api } = useAuth()
  const [campaigns, setCampaigns] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stale, setStale] = useState(false)
  const [search, setSearch] = useState('')
  const [statusTab, setStatusTab] = useState('')
  const [status, setStatus] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const loadCampaigns = useCallback(async () => {
    try {
      const qs = statusTab ? `?status=${statusTab}` : ''
      const data = await api(`/sponsors/campaigns${qs}`)
      setCampaigns(data.campaigns || [])
      setTotal(data.total || (data.campaigns?.length ?? 0))
      setStale(!!data.stale)
      if (data.ok === false) {
        setStatus(`Error: ${data.error?.message || 'AdSpeed rejected the filter'}`)
      } else {
        setStatus('')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, statusTab])

  useEffect(() => {
    setLoading(true)
    loadCampaigns().finally(() => setLoading(false))
  }, [loadCampaigns])

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await api('/sponsors/cache/bust?prefix=campaigns:', { method: 'POST' })
      await loadCampaigns()
      setStatus('Refreshed from AdSpeed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setRefreshing(false)
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return campaigns
    return campaigns.filter(c => {
      const id = String(campaignField(c, 'id', 'CampaignId') || '')
      const name = String(campaignField(c, 'name', 'Name') || '').toLowerCase()
      return name.includes(q) || id.toLowerCase().includes(q)
    })
  }, [campaigns, search])

  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let av, bv
      if (sortBy === 'id') {
        av = Number(campaignField(a, 'id', 'CampaignId') || 0)
        bv = Number(campaignField(b, 'id', 'CampaignId') || 0)
      } else {
        const k = sortBy
        const K = k.charAt(0).toUpperCase() + k.slice(1)
        av = String(campaignField(a, k, K) || '').toLowerCase()
        bv = String(campaignField(b, k, K) || '').toLowerCase()
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sortBy, sortDir])

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Campaigns</h1>
          <span className="sponsors-count">{filtered.length} of {total}</span>
          {stale && (
            <span className="sponsors-stale-badge">
              <AlertTriangle size={11} /> Stale cache
            </span>
          )}
        </div>
        <div className="sponsors-header-right">
          <button
            className="sponsors-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="muted" style={{ marginBottom: 12, fontSize: '0.85rem' }}>
        Read-only — AdSpeed's API doesn't expose campaign create / edit / delete.
        Use the AdSpeed dashboard to manage campaigns.
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="adtag-format-row" style={{ marginBottom: 10 }}>
        {STATUS_TABS.map(t => (
          <button
            key={t.value || 'all'}
            className={`adtag-format-btn ${statusTab === t.value ? 'active' : ''}`}
            onClick={() => setStatusTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="zones-toolbar">
        <input
          className="zones-search"
          placeholder="Filter by name or id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="muted">Loading campaigns…</div>
      ) : sorted.length === 0 ? (
        <div className="muted">No campaigns found.</div>
      ) : (
        <table className="zones-table">
          <thead>
            <tr>
              {[
                { key: 'id',     label: 'ID' },
                { key: 'name',   label: 'Name' },
                { key: 'status', label: 'Status' },
              ].map(c => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  {c.label}
                  {sortBy === c.key && <span style={{ marginLeft: 4, color: '#f59e0b' }}>
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </span>}
                </th>
              ))}
              <th style={{ width: 140 }}>AdSpeed</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => {
              const id = String(campaignField(c, 'id', 'CampaignId'))
              const url = `https://advertiser.tenband.tv/Campaigns/show.html?id=${id}`
              return (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{campaignField(c, 'name', 'Name')}</td>
                  <td>{campaignField(c, 'status', 'Status')}</td>
                  <td>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sponsors-refresh-btn"
                      style={{ fontSize: '0.78rem', textDecoration: 'none' }}
                    >
                      <ExternalLink size={11} /> View
                    </a>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
